import { useCallback, useMemo } from 'react'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useRouteError,
} from 'react-router'
import { ErrorBoundary as AppSignalErrorBoundary } from '@appsignal/react'
import type { Span } from '@appsignal/javascript'
import appsignal from '~/appsignal'
import { scrubIdsFromPath } from '~/utils/scrubPath'
import { useVersionChecker } from '~/hooks/useVersionChecker'
import './index.css'
// Side-effect: applies stored/system theme on load + registers OS preference listener
import '~/stores/themeStore'
// Initialises i18next; the language follows the deployment region.
import i18n from '~/i18n'

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang={i18n.language} suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" href={`${import.meta.env.BASE_URL}favicon.ico`} sizes="32x32" />
        <link rel="icon" href={`${import.meta.env.BASE_URL}assets/icon.svg`} type="image/svg+xml" />
        <link rel="apple-touch-icon" href={`${import.meta.env.BASE_URL}assets/apple-touch-icon.png`} />
        <script src={`${import.meta.env.BASE_URL}env.js`}></script>
        {/* Apply stored theme before React hydrates to prevent mismatch */}
        <script dangerouslySetInnerHTML={{ __html: `try{var t=localStorage.getItem('tmc-theme-preference')||'auto';if(t==='auto')t=matchMedia('(prefers-color-scheme:dark)').matches?'dark':'light';document.documentElement.setAttribute('data-theme',t)}catch(e){}` }} />
        <title>TMC - Cortex</title>
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <div id="x-uuid" data-version={__BUILD_ID__} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

function Fallback() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <p>Er is een onverwachte fout opgetreden.</p>
      <button
        type="button"
        className="rounded-lg bg-moss px-5 py-2 text-[var(--color-on-primary)]"
        onClick={() => window.location.reload()}
      >
        Pagina herladen
      </button>
    </div>
  )
}

export default function App() {
  const location = useLocation()
  useVersionChecker()

  const tags = useMemo(() => ({ app: 'frontEmplNew' }), [])
  const fallback = useCallback(() => <Fallback />, [])
  const override = useCallback(
    (span: Span) => {
      // Scrub patient/consult ids: telemetry leaves the org (AppSignal) and
      // the scrubbed path is just as useful for debugging.
      span.setTags({ path: scrubIdsFromPath(location.pathname) })
      span.setParams({ url: window.location.origin + scrubIdsFromPath(window.location.pathname) })
      return span
    },
    [location.pathname]
  )

  // If AppSignal is not initialised (dev, or no key), render without the boundary.
  if (!appsignal) {
    return <Outlet />
  }

  return (
    <AppSignalErrorBoundary
      instance={appsignal}
      action="App"
      tags={tags}
      override={override}
      fallback={fallback}
    >
      <Outlet />
    </AppSignalErrorBoundary>
  )
}

export function HydrateFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-beige-shade border-t-moss" />
    </div>
  )
}

export function ErrorBoundary() {
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    return (
      <main className="p-6">
        <h1 className="text-xl font-semibold">
          {error.status} {error.statusText}
        </h1>
        {error.data ? <pre className="mt-2 text-sm">{String(error.data)}</pre> : null}
      </main>
    )
  }

  const message = error instanceof Error ? error.message : 'Onbekende fout'
  return (
    <main className="p-6">
      <h1 className="text-xl font-semibold">Onverwachte fout</h1>
      <pre className="mt-2 text-sm">{message}</pre>
    </main>
  )
}
